import { PropsWithChildren, ReactNode, useMemo, useState } from 'react';
import DiscountIllustration from '../assets/illustrations/DiscountIllustration';
import { useCallback, useEffect } from 'react';
import { ButtonTypes, CouponDetailsInterface, PromoComponentInterface } from '../services/exports/Interfaces';
import { useSelector } from 'react-redux';
import { collect } from 'collect.js';
import { COUPON_TYPES, MODAL_SIZES, PROMO_CODE_DISCOUNT_TYPES } from '../services/exports/Constants';
import { Check, X } from 'lucide-react';
import useTheme from '../hooks/ui/useTheme';
import useScreenType from '../hooks/utility/useScreenType';
import CustomModal from '../components-library/CustomModal';
import LoadingIndicator from '../components-library/loading/LoadingIndicator';
import { useTranslation } from 'react-i18next';
import { StoreInterface } from '../store/types';
import useAppMethods from '../hooks/utility/useAppMethods';
import usePromoCode from '../hooks/order/usePromoCode';
import useAuth from '../hooks/global/useAuth';
import useMobileApp from '../hooks/mobile-app/useMobileApp';
import useModalRouter from '../modules/mobile/common/hooks/useModalRouter';
import { Modals } from '../modules/mobile/exports/Enums';
import { resolvePromoApplyAction } from './promo-apply-action';
import DiscountGeneralNote from '../features/discount-exclusivity/DiscountGeneralNote';
import DiscountComparisonChoice from '../features/discount-exclusivity/DiscountComparisonChoice';
import useActiveDiscount from '../features/discount-exclusivity/useActiveDiscount';
import useShowExclusivityNote from '../features/discount-exclusivity/useShowExclusivityNote';
import useActiveOffers from '../features/discount-exclusivity/useActiveOffers';

interface Props {
  openModal: boolean;
  toggleModal: () => void;
  /** When opened from a card in PREVIEW (not yet applied), the promo to show + apply. */
  previewPromoCode?: CouponDetailsInterface | null;
}

export default function MultiPromoCodeModal(props: Props) {
  const { t } = useTranslation(null, { keyPrefix: 'Modals:MultiPromoCodeModal' });

  const { openModal, toggleModal, previewPromoCode } = props;

  const { cached_order } = useSelector((state: StoreInterface) => state.order);

  const { formatCurrency, toggleLoginModal } = useAppMethods();
  const { removePromoCode, applyPromoCode } = usePromoCode();
  const { isLoggedIn } = useAuth();
  const { isMobileApp } = useMobileApp();
  const { openModal: openMobileModal } = useModalRouter();
  const theme = useTheme();
  const { isDesktop } = useScreenType();

  // Show the previewed promo (card-first flow) or, failing that, the applied one.
  const detail = previewPromoCode ?? cached_order?.coupon_details;
  const isApplied =
    cached_order?.coupon_type === COUPON_TYPES.PROMO_CODE && cached_order?.coupon_details?.code === detail?.code;

  // When a conflicting discount (offer/gift/points) is active and this promo isn't applied yet,
  // show the comparison-choice ("Keep offer / Use code instead") instead of a plain Apply button.
  const activeDiscount = useActiveDiscount();
  const showComparison = !isApplied && !!activeDiscount && activeDiscount.kind !== 'promo';
  // UX point 7 (PROD-407): only show the exclusivity note when a discount is already applied that
  // applying this promo would displace — with nothing applied there is no conflict, so no note.
  const showGeneralNote = useShowExclusivityNote('promo_or_gift');
  // What this code saves on the immediate order (first tier), for the "The code saves €X" line.
  const candidateSaving = detail?.components?.[0]?.discount ?? null;

  // When SEVERAL offers are active, the comparison card summarises them ("You have {n} active
  // offers: … Promo code X saves …") instead of naming one — item 6.
  const { t: tComparison } = useTranslation(null, { keyPrefix: 'Features:DiscountExclusivity.comparison' });
  const activeOffers = useActiveOffers();
  const multiOfferMessage =
    showComparison && activeDiscount?.kind === 'offer' && activeOffers.count > 1
      ? tComparison(activeOffers.count > 2 ? 'active_offers_more' : 'active_offers_two', {
          count: activeOffers.count,
          first: activeOffers.offers[0].label,
          firstValue: formatCurrency(activeOffers.offers[0].value),
          second: activeOffers.offers[1].label,
          secondValue: formatCurrency(activeOffers.offers[1].value),
          more: activeOffers.count - 2,
          total: formatCurrency(activeOffers.totalValue),
          code: detail?.code ?? '',
          codeValue: formatCurrency(candidateSaving ?? 0),
        })
      : undefined;

  const onRemove = () => {
    removePromoCode();
    toggleModal();
  };

  // A logged-out customer must sign in first. `applyPromoCode` only flips a `showLoginPrompt` flag
  // that nothing renders, so from this modal it looked like "nothing happens" on Apply — open the
  // login modal instead (mirrors the deals-card path). Close this modal first so the login modal
  // isn't hidden behind it.
  const onLogin = () => {
    toggleModal();
    if (isMobileApp) {
      return openMobileModal({ id: Modals.Login });
    }

    toggleLoginModal({ openModal: true, onCloseRedirect: () => null });
  };

  const onApply = () => {
    const action = resolvePromoApplyAction({ hasCode: !!detail?.code, isLoggedIn });
    if (action === 'login') {
      return onLogin();
    }

    if (action === 'apply' && detail?.code) {
      applyPromoCode(detail.code);
    }
  };

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (openModal) {
      onDidFocus();
    }
  }, [openModal]);

  function onDidFocus() {
    if (!isDesktop) {
      setLoading(true);
      setTimeout(() => setLoading(false), 1000);
    }
  }

  const totalSaved = useCallback(() => {
    return collect(detail?.components)?.reduce(
      (carry: number, component: PromoComponentInterface) => carry + component?.discount,
    );
  }, [detail]);

  function isActive(component: PromoComponentInterface, index: number): boolean {
    if (index === 0) {
      return !component?.is_used;
    }

    return !component?.is_used && collect(detail?.components)?.get(index - 1)?.is_used;
  }

  function renderList() {
    function renderDiscount(component: PromoComponentInterface) {
      switch (detail?.discount_type) {
        case PROMO_CODE_DISCOUNT_TYPES.PERCENTAGE:
          return t('discounts.percentage', {
            discount: component?.discount,
          });
        case PROMO_CODE_DISCOUNT_TYPES.FIXED_AMOUNT:
          return t('discounts.fixed', {
            discount: formatCurrency(component?.discount),
          });
      }
    }

    function getBackgroundColor(component: PromoComponentInterface, active: boolean) {
      if (component?.is_used) {
        return theme.isDarkMode ? theme.color.black[600] : theme.color.gray.DEFAULT;
      }

      if (active) {
        return theme.buttons[ButtonTypes.primary].background;
      }

      return theme.color.brand.card;
    }

    function getSubTextColor(component: PromoComponentInterface, active: boolean) {
      if (component?.is_used) {
        return theme.color.white['100'];
      }

      if (active) {
        return theme.buttons[ButtonTypes.primary].text;
      }

      return theme.color.gray['300'];
    }

    function getDiscountColor(component: PromoComponentInterface, active: boolean) {
      if (component?.is_used) {
        return theme.color.white['100'];
      }

      if (active) {
        return theme.buttons[ButtonTypes.primary].text;
      }

      return theme.color.brand.primary;
    }

    return (
      <div className={listContainer(detail?.components?.length)}>
        {detail?.components?.map((component: PromoComponentInterface, index: number) => {
          const active = isActive(component, index);

          return (
            <div
              className={itemContainer}
              style={{
                backgroundColor: getBackgroundColor(component, active),
              }}
              key={component?.id?.toString()}
            >
              <p style={{ color: getSubTextColor(component, active) }} className="text-xs font-medium">
                {t('list.item.order', {
                  orderNumber: index + 1,
                })}
              </p>
              <h3 className="text-base font-bold" style={{ color: getDiscountColor(component, active) }}>
                {renderDiscount(component)}
              </h3>
            </div>
          );
        })}
      </div>
    );
  }

  function renderButton() {
    // A conflicting discount is active → the comparison card (in the content) owns the action.
    if (showComparison) {
      return null;
    }

    // Not applied yet (preview from a card) → the customer must click Apply, per the prototype.
    if (!isApplied) {
      return (
        <div className={buttonContainer(isMobileApp)}>
          <button
            type="button"
            onClick={onApply}
            className="w-full rounded-full bg-brand-primary py-3.5 font-semibold text-brand-button-primary-text transition-opacity hover:opacity-90"
          >
            {t('apply')}
          </button>
        </div>
      );
    }

    return (
      <div className={buttonContainer(isMobileApp)}>
        <button
          type="button"
          disabled
          className="flex flex-1 items-center justify-center gap-2 rounded-full bg-success/15 py-3.5 font-semibold text-success"
        >
          <Check className="w-4 h-4" />
          {t('applied')}
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="text-sm font-semibold text-brand-primary underline-offset-4 hover:underline"
        >
          {t('remove')}
        </button>
      </div>
    );
  }

  function renderTitle() {
    switch (detail?.discount_type) {
      case PROMO_CODE_DISCOUNT_TYPES.PERCENTAGE:
        return t('savings.percentage', {
          totalSaved: totalSaved(),
        });
      case PROMO_CODE_DISCOUNT_TYPES.FIXED_AMOUNT:
        return t('savings.fixed', {
          totalSaved: formatCurrency(totalSaved()),
        });
    }
  }

  const renderLoadingPage = useMemo(() => {
    return loading ? (
      <div className={loadingContainer}>
        <div className="-mb-1.5">
          <LoadingIndicator lg={60} md={50} sm={40} />
        </div>
      </div>
    ) : null;
  }, [loading]);

  return (
    <WithWrapper openModal={openModal} onClose={toggleModal}>
      <div className={container(isMobileApp)}>
        <button
          type="button"
          aria-label={t('custom_button.done')}
          onClick={toggleModal}
          className="absolute right-md top-md z-10 text-muted-foreground hover:text-brand-text-primary"
          data-test="multi-promo-close"
        >
          <X className="w-5 h-5" />
        </button>
        <div className={wrapper(isMobileApp)}>
          <div className={illustrationContainer}>
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-primary/10">
              <DiscountIllustration
                className="w-9 h-9"
                color={theme.buttons[ButtonTypes.primary].background}
                starColor={theme.buttons[ButtonTypes.primary].text}
              />
            </div>
          </div>
          <h3 className="text-center mb-sm">{renderTitle()}</h3>
          <p className="text-center text-muted-foreground mb-sm">
            {t('description', {
              code: detail?.code,
              count: detail?.components?.length,
            })}
          </p>
          {/* Grey general note only when the comparison card is NOT shown (otherwise they repeat
              the same one-per-order rule, CTO 2026-08-03) AND a discount is already applied that
              this promo would displace (UX point 7 — no message when there is no conflict). */}
          {!showComparison && showGeneralNote && <DiscountGeneralNote className="mb-lg" />}
          {renderList()}
          {showComparison && activeDiscount && (
            <DiscountComparisonChoice
              className="mt-lg"
              active={multiOfferMessage ? { ...activeDiscount, saving: activeOffers.totalValue } : activeDiscount}
              candidate={{ kind: 'promo', label: detail?.code ?? '', saving: candidateSaving }}
              onUseInstead={onApply}
              onKeep={toggleModal}
              messageOverride={multiOfferMessage}
            />
          )}
        </div>
        {renderButton()}
        {renderLoadingPage}
      </div>
    </WithWrapper>
  );
}

// In the mobile app the modal is presented natively by the Ion `ModalRouter` (a swipeable sheet
// that respects the device safe areas), so here we render just the content — mirrors `OfferModal`'s
// `WithWrapper` (FOO-3200). On web/mobile-web it keeps the generic `CustomModal`.
const WithWrapper = ({
  openModal,
  onClose,
  children,
}: PropsWithChildren<{ openModal: boolean; onClose: () => void }>): ReactNode => {
  const { isMobileApp } = useMobileApp();
  const { isDesktop } = useScreenType();

  if (isMobileApp) {
    return children;
  }

  return (
    <CustomModal open={openModal} size={MODAL_SIZES.XS} fullScreen={!isDesktop} onClose={onClose}>
      {children}
    </CustomModal>
  );
};

// In the native Ion sheet (mobile app) the content flows naturally inside `IonContent` and the
// action bar sits in-flow, so no full-screen scroll container / fixed button / bottom clearance is
// needed. On web (incl. mobile-web) the CustomModal is full-screen with a fixed bottom button — the
// body must scroll and the content needs bottom padding so the last tier card clears the button; on
// desktop it's a compact centered dialog with the button in flow.
const container = (isMobileApp: boolean) =>
  isMobileApp
    ? 'w-full min-h-[90dvh] flex flex-col relative'
    : 'w-full h-full flex flex-col relative overflow-y-auto lg:justify-center lg:overflow-visible';

// On the app the wrapper grows (flex-1) so the in-flow action bar is pushed to the bottom of the
// sheet, clear of the home indicator.
const wrapper = (isMobileApp: boolean) =>
  isMobileApp ? 'w-full px-md pt-lg pb-md flex-1' : 'w-full lg:px-lg px-md pt-lg pb-28 lg:pb-lg lg:h-fit';

const listContainer = (length: number) => `grid lg:grid-cols-${length} grid-cols-1 gap-4`;

const itemContainer =
  'flex flex-col justify-center items-center gap-2xs rounded-2xl border-xs border-brand-border px-lg py-md transition-colors';

const illustrationContainer = 'flex justify-center mb-md';

// `browser-safe-area-bottom` = 12px + env(safe-area-inset-bottom): the fixed web bar clears the
// home indicator on mobile-web, and the in-flow app bar clears it at the bottom of the Ion sheet.
// App: in-flow (relative) inside IonContent. Web: fixed to the bottom of the full-screen CustomModal.
// App: `sticky bottom-0` keeps the action bar pinned to the bottom of the Ion sheet, always visible
// even when the tiers overflow and scroll (the `flex-1` wrapper still pushes it down when content is
// short). Web: fixed to the bottom of the full-screen CustomModal. Both clear the home indicator.
const buttonContainer = (isMobileApp: boolean) =>
  isMobileApp
    ? 'w-full flex items-center gap-md px-md py-sm border-t border-brand-border bg-brand-background-primary sticky bottom-0 browser-safe-area-bottom'
    : 'w-full flex items-center gap-md lg:px-lg px-md py-sm border-t border-brand-border bg-brand-background-primary lg:relative fixed bottom-0 browser-safe-area-bottom';

const loadingContainer = 'absolute inset-0 flex flex-col justify-center items-center';
